// (C) Copyright 2015 Moodle Pty Ltd.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { Injectable } from '@angular/core';
import { CoreSite } from '@classes/sites/site';
import { FreemiumSitesProvider } from '@freemium/overrides/core/services/sites';
import { SiteDBEntry } from '@services/database/sites';
import { makeSingleton } from '@singletons';
import { CoreEvents } from '@singletons/events';

/**
 * Override sites provider to add functionality specific to Workplace.
 */
@Injectable({ providedIn: 'root' })
export class WorkplaceSitesProvider extends FreemiumSitesProvider {

    /**
     * @inheritdoc
     */
    protected isWorkplaceEnabled(): boolean {
        return true;
    }

    /**
     * @inheritdoc
     */
    async addSiteFromSiteListEntry(entry: SiteDBEntry): Promise<CoreSite> {
        const site = await super.addSiteFromSiteListEntry(entry);
        await site.loadTenantUrls();

        return site;
    }

    /**
     * @inheritdoc
     */
    async newSite(
        siteUrl: string,
        token: string,
        privateToken: string = '',
        login: boolean = true,
        oauthId?: number,
    ): Promise<string> {
        const siteId = await super.newSite(siteUrl, token, privateToken, login, oauthId);

        const site = await this.getSite(siteId);

        const tenantUrl = site.getInfo()?.siteurl;
        this.setTenantSiteUrl(site, tenantUrl);

        return siteId;
    }

    /**
     * @inheritdoc
     */
    async updateSiteInfo(siteId?: string): Promise<void> {
        const site = await this.getSite(siteId);

        try {
            await super.updateSiteInfo(siteId);
            const tenantUrl = site.getInfo()?.siteurl;

            this.setTenantSiteUrl(site, tenantUrl);
        } catch {
            // Ignore that we cannot fetch site info. Probably the auth token is invalid.
        }
    }

    /**
     * Set the tenant site URL.
     *
     * @param site Site object.
     * @param siteUrl New site URL.
     */
    protected async setTenantSiteUrl(site: CoreSite, siteUrl?: string): Promise<void> {
        if (!siteUrl || site.siteUrl === siteUrl) {
            return;
        }

        site.siteUrl = siteUrl;

        const newValues: Partial<SiteDBEntry> = {
            siteUrl,
        };
        const siteId = site.getId();

        try {
            await this.sitesTable.update(newValues, { id: siteId });
        } finally {
            CoreEvents.trigger(CoreEvents.SITE_UPDATED, site.getInfo(), siteId);
        }
    }

}

export const WorkplaceSites = makeSingleton<WorkplaceSitesProvider>(WorkplaceSitesProvider);
